import React from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import ReactFlow, { Background } from "reactflow";
import "reactflow/dist/style.css";
import CircleNode from "./CircleNode";
import { useTranslation } from "react-i18next";

const SimsLifecycle = () => {
  const { t } = useTranslation();
  // Define lifecycle nodes
  const nodes = [
    {
      id: "productive",
      type: "circle",
      position: { x: 400, y: 200 },
      data: { label: "Productive", color: "#007bff" },
    },
    {
      id: "onstock",
      type: "circle",
      position: { x: 200, y: 200 },
      data: { label: "OnStock", color: "#6c757d" },
    },
    {
      id: "suspended",
      type: "circle",
      position: { x: 600, y: 120 },
      data: { label: "Suspended", color: "#ffc107" },
    },
    {
      id: "deleted",
      type: "circle",
      position: { x: 400, y: 320 },
      data: { label: "Deleted", color: "#dc3545" },
    },
  ];

  // Define transitions
  const edges = [
    {
      id: "e1",
      source: "productive",
      sourceHandle: "s-top-right",
      target: "suspended",
      targetHandle: "t-left",
      label: "Suspend",
      type: "smoothstep",
      style: { stroke: "orange" },
      markerEnd: { type: "arrowclosed", color: "#ffc107" },
    },
    {
      id: "e2",
      source: "suspended",
      sourceHandle: "s-bottom-left",
      target: "productive",
      targetHandle: "t-right",
      label: "Resume",
      type: "smoothstep",
      style: { stroke: "#007bff" },
      markerEnd: { type: "arrowclosed", color: "#007bff" },
    },
    {
      id: "e3",
      source: "productive",
      sourceHandle: "s-bottom-left",
      target: "onstock",
      targetHandle: "t-bottom-right",
      label: "Termination",
      type: "smoothstep",
      style: { stroke: "red" },
      markerEnd: { type: "arrowclosed", color: "red" },
    },
    {
      id: "e7",
      source: "suspended",
      sourceHandle: "s-top-left",
      target: "onstock",
      targetHandle: "t-top",
      label: "Termination",
      type: "smoothstep",
      style: { stroke: "red" },
      markerEnd: { type: "arrowclosed", color: "red" },
    },
    {
      id: "e9",
      source: "suspended",
      sourceHandle: "bottom",
      target: "deleted",
      targetHandle: "t-right",
      label: "Termination",
      type: "smoothstep",
      style: { stroke: "red" },
      markerEnd: { type: "arrowclosed", color: "red" },
    },
    {
      id: "e11",
      source: "onstock",
      sourceHandle: "s-top-right",
      target: "productive",
      targetHandle: "t-top-left",
      label: "Activation",
      type: "smoothstep",
      style: { stroke: "blue" },
      markerEnd: { type: "arrowclosed", color: "blue" },
    },
    {
      id: "e12",
      source: "productive",
      sourceHandle: "bottom",
      target: "deleted",
      targetHandle: "t-top",
      label: "Termination",
      type: "smoothstep",
      style: { stroke: "red" },
      markerEnd: { type: "arrowclosed", color: "red" },
    },
  ];

  const nodeTypes = { circle: CircleNode };

  return (
    <Container fluid className="py-5 sims-lifecycle">
      <Row className="justify-content-center mb-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 rounded-lg p-4">
            <Card.Body>
              <Card.Title className="display-6 mb-3">
                {t("documentation.simCards.lifecycle.title")}
              </Card.Title>
              <Card.Text>
                {t("documentation.simCards.lifecycle.intro")}
              </Card.Text>
              <ul>
                <li>
                  <Badge bg="primary">Productive</Badge> –{" "}
                  {t("documentation.simCards.lifecycle.status.productive")}
                </li>
                <li>
                  <Badge bg="warning" text="dark">
                    Suspended
                  </Badge>{" "}
                  – {t("documentation.simCards.lifecycle.status.suspended")}
                </li>
                <li>
                  <Badge bg="warning" text="dark">
                    AutoSuspended
                  </Badge>{" "}
                  – {t("documentation.simCards.lifecycle.status.autoSuspended")}
                </li>
                <li>
                  <Badge bg="secondary">OnStock</Badge> –{" "}
                  {t("documentation.simCards.lifecycle.status.onstock")}
                </li>
                <li>
                  <Badge bg="danger">Deleted</Badge> –{" "}
                  {t("documentation.simCards.lifecycle.status.deleted")}
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={10} className="mx-auto">
          <Card className="shadow-sm border-0 rounded-lg p-3">
            <div
              style={{
                height: "60vh",
                minHeight: "400px",
                maxHeight: "650px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ReactFlow
                nodes={nodes}
                nodeTypes={nodeTypes}
                edges={edges}
                fitView
                fitViewOptions={{ padding: 0.3 }}
                zoomOnScroll={false}
                zoomOnPinch={false}
                panOnDrag={false}
                panOnScroll={false}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                selectionOnDrag={false}
                onMouseMove={(e) => e.preventDefault()}
                autoPanOnNodeDrag={false} // ⬅️ stop auto-pan when dragging nodes
                autoPanOnConnect={false} // ⬅️ stop auto-pan when starting a connection
                proOptions={{ hideAttribution: true }}
                onInit={(instance) => {
                  window.addEventListener("resize", () =>
                    instance.fitView({ padding: 0.3 })
                  );
                }}
              >
                <Background color="#f5f5f5" gap={20} />
              </ReactFlow>
            </div>

            {/* ✅ Description under diagram */}
            <Card.Body className="pt-4">
              <Card.Text className="text-muted" style={{ fontSize: "0.9rem" }}>
                {t("documentation.simCards.lifecycle.diagramDescription")}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SimsLifecycle;
